<template>
  <div class="tf an">
    <Breadcrumbs main="Аналитика" :title="sourceTitle"/>

    <div class="container-fluid tf-wrap">
      <div class="tf-topbar">
        <div class="tf-topbar__title">
          <div class="tf-topbar__name">{{ sourceTitle }}</div>
          <div class="tf-topbar__meta">
            <span>{{ page.subtitle }}</span>
          </div>
        </div>

        <div class="tf-topbar__spacer"></div>

        <div class="tf-topbar__actions">
          <div class="an-seg" role="group" aria-label="Период">
            <button v-for="option in periods" :key="option.value" type="button"
                    class="an-seg__btn" :class="{ 'is-on': period === option.value }"
                    @click="setPeriod(option.value)">
              {{ option.label }}
            </button>
          </div>

          <button v-if="isRum" type="button" class="tf-btn-ghost" @click="$bvModal.show('analytics-glossary')">
            <i class="fa fa-book"></i> Словарь
          </button>

          <button type="button" class="tf-icon-btn" :class="{ 'tf-spin': isLoading }"
                  @click="reload" v-b-tooltip.hover.bottom="'Обновить'">
            <i class="fa fa-refresh"></i>
          </button>
        </div>
      </div>

      <!-- сервис не ответил или не пустил: страницу дальше не рисуем -->
      <div v-if="sourcesError" class="tf-card">
        <div class="an-empty">
          <i class="fa" :class="sourcesError.icon"></i>
          <span class="an-empty__title">{{ sourcesError.title }}</span>
          <span>{{ sourcesError.text }}</span>
        </div>
      </div>

      <div v-else-if="sourcesLoading && !source" class="an-sk"></div>

      <template v-else-if="source">
        <div v-if="!source.enabled" class="tf-card">
          <div class="an-empty">
            <i class="fa fa-plug"></i>
            <span class="an-empty__title">Источник не подключён</span>
            <span>На сервере аналитики не заданы ключи для «{{ source.title }}»</span>
          </div>
        </div>

        <template v-else>
          <div class="an-status">
            <span>Данные обновлены: {{ formatDateTime(currentReport.fetchedAt || source.lastOkAt) }}</span>
            <span v-if="source.lastError" class="tf-badge tf-badge--warn" :title="source.lastError">
              последнее обновление не удалось
            </span>
          </div>

          <div v-if="currentReport.stale" class="tf-alert tf-alert--warn">
            <i class="fa fa-exclamation-triangle tf-alert__icon"></i>
            <div class="tf-alert__body">
              <div class="tf-alert__title">Цифры давно не обновлялись — показан последний удачный снимок</div>
            </div>
          </div>

          <div class="an-tabs" role="tablist">
            <button v-for="report in source.reports" :key="report.id" type="button" role="tab"
                    class="an-tabs__btn" :class="{ 'is-on': tab === report.id }"
                    @click="setTab(report.id)">
              {{ report.title }}
            </button>
          </div>

          <RumGuide v-if="isRum" :key="'guide-' + tab" :tab="tab"/>

          <div v-if="currentReport.state === 'loading'" class="an-sk"></div>

          <div v-else-if="currentReport.state === 'pending'" class="tf-card">
            <div class="an-empty">
              <i class="fa fa-hourglass-half"></i>
              <span class="an-empty__title">Данные ещё собираются</span>
              <span>Сервис забирает отчёт раз в несколько минут — загляните чуть позже</span>
            </div>
          </div>

          <div v-else-if="currentReport.state === 'error'" class="tf-card">
            <div class="an-empty">
              <i class="fa fa-exclamation-circle"></i>
              <span class="an-empty__title">Отчёт не загрузился</span>
              <span>{{ currentReport.error }}</span>
            </div>
          </div>

          <template v-else-if="currentReport.state === 'ready'">
            <RumFacts v-if="isRum" :facts="facts"/>
            <component v-if="reportComponent" :is="reportComponent"
                       :data="currentReport.data" :is-dark="isDark"/>
            <div v-else class="tf-card">
              <div class="an-empty">
                <i class="fa fa-wrench"></i>
                <span>Для этого отчёта в админке ещё нет отображения</span>
              </div>
            </div>
          </template>
        </template>
      </template>
    </div>

    <b-modal id="analytics-glossary" title="Словарь" size="lg" hide-footer scrollable>
      <dl class="an-glossary">
        <template v-for="item in glossary">
          <dt :key="'t' + item.term">{{ item.term }}</dt>
          <dd :key="'d' + item.term">{{ item.text }}</dd>
        </template>
      </dl>
    </b-modal>
  </div>
</template>

<script>
import analyticsApi from "@/api/nodes/api.analytics";
import RumOverview from "@/components/Analytics/RumOverview.vue";
import RumPages from "@/components/Analytics/RumPages.vue";
import RumAudience from "@/components/Analytics/RumAudience.vue";
import RumReleases from "@/components/Analytics/RumReleases.vue";
import RumActions from "@/components/Analytics/RumActions.vue";
import RumProduct from "@/components/Analytics/RumProduct.vue";
import RumApi from "@/components/Analytics/RumApi.vue";
import RumScripts from "@/components/Analytics/RumScripts.vue";
import RumHealth from "@/components/Analytics/RumHealth.vue";
import RumGuide from "@/components/Analytics/RumGuide.vue";
import RumFacts from "@/components/Analytics/RumFacts.vue";
import {formatDateTime} from "@/common/rumFormat";
import {factsFor} from "@/common/rumFacts";
import {GLOSSARY} from "@/common/rumGuide";
import ViewBoard from "@/components/Analytics/view/ViewBoard.vue";

// RUM сервис хранит как есть — у каждого раздела свой компонент.
// Сторонние системы (Amplitude, дальше Метрика и GA4) приходят в общем виде
// блоков, их все рисует ViewBoard.
const REPORT_COMPONENTS = {
  'rum/overview': RumOverview,
  'rum/pages': RumPages,
  'rum/audience': RumAudience,
  'rum/releases': RumReleases,
  'rum/actions': RumActions,
  'rum/product': RumProduct,
  'rum/api': RumApi,
  'rum/scripts': RumScripts,
  'rum/health': RumHealth,
}

/**
 * Сайт отвечает обёрткой {section, from, to, filter, data}, сервис хранит её
 * как есть. Компонентам нужен сам отчёт.
 */
export function unwrapRumReport(payload) {
  return (payload && payload.data) || {}
}

const PERIODS = [
  {value: '1d', label: 'Сутки'},
  {value: '7d', label: 'Неделя'},
  {value: '30d', label: 'Месяц'},
]

/**
 * Страница одна на все источники, какой показать — в meta маршрута
 * (analyticsSource). Новый источник — строка здесь, маршрут и пункт меню.
 */
export const SOURCE_PAGES = {
  rum: {
    title: 'Скорость сайта',
    subtitle: 'Как сайт работает у настоящих посетителей',
    missing: 'Сервис аналитики не знает про отчёты скорости сайта',
  },
  amplitude: {
    title: 'Продукт',
    subtitle: 'Что делают пользователи на сайте — по событиям Amplitude',
    missing: 'Сервис аналитики не знает про отчёты Amplitude — возможно, на сервере старая версия',
  },
}

export function sourceIdOf(route) {
  const id = route?.meta?.analyticsSource
  return SOURCE_PAGES[id] ? id : 'rum'
}

/** Почему сервис не отдал список источников — человеческими словами. */
export function describeServiceError(e) {
  const status = e?.response?.status
  if (!e?.response) {
    return {
      icon: 'fa-chain-broken',
      title: 'Сервис аналитики недоступен',
      text: 'Сервер не ответил. Если это прод — сервис туда, возможно, ещё не выкачен.',
    }
  }
  if (status === 403) {
    return {icon: 'fa-lock', title: 'Нет доступа', text: 'Аналитика доступна только администраторам'}
  }
  if (status === 503) {
    return {
      icon: 'fa-clock-o',
      title: 'Не удалось проверить права',
      text: 'Основной бэкенд не ответил сервису аналитики. Попробуйте через минуту.',
    }
  }

  return {
    icon: 'fa-exclamation-circle',
    title: 'Ошибка сервиса аналитики',
    text: e.response.data?.error || `Сервер ответил ${status}`,
  }
}

export default {
  name: "AnalyticsPage",
  components: {RumGuide, RumFacts, ViewBoard},
  data() {
    const query = this.$route.query || {}
    return {
      periods: PERIODS,
      glossary: GLOSSARY,
      period: PERIODS.some(p => p.value === query.period) ? query.period : '7d',
      tab: typeof query.tab === 'string' ? query.tab : '',

      source: null,
      sourcesLoading: false,
      sourcesError: null,

      // ключ `${источник}:${отчёт}:${период}` → {state, data, fetchedAt, stale, error}
      reports: {},

      isDark: false,
      themeObserver: null,
    }
  },

  computed: {
    sourceId() {
      return sourceIdOf(this.$route)
    },
    isRum() {
      return this.sourceId === 'rum'
    },
    page() {
      return SOURCE_PAGES[this.sourceId]
    },
    sourceTitle() {
      // У сервиса заголовок источника технический («Продукт (Amplitude)»),
      // в админке — пункт меню.
      return this.page.title
    },
    reportKey() {
      // Источник в ключе: ответ старого источника, пришедший после перехода
      // на другой пункт меню, не ляжет под ту же вкладку и период.
      return `${this.sourceId}:${this.tab}:${this.period}`
    },
    currentReport() {
      return this.reports[this.reportKey] || {state: 'loading'}
    },
    reportComponent() {
      if (!this.isRum) return ViewBoard
      return REPORT_COMPONENTS[`rum/${this.tab}`] || null
    },
    facts() {
      return this.isRum && this.currentReport.state === 'ready' ? factsFor(this.tab, this.currentReport.data) : []
    },
        isLoading() {
      return this.sourcesLoading || this.currentReport.state === 'loading'
    },
  },

  watch: {
    // /analytics и /analytics/product — одна страница: роутер переиспользует
    // компонент, поэтому переход между пунктами меню — это смена источника.
    sourceId() {
      this.source = null
      this.reports = {}
      this.tab = ''
      this.loadSources()
    },
  },

  created() {
    this.loadSources()
  },

  mounted() {
    this.detectTheme()
    this.themeObserver = new MutationObserver(() => this.detectTheme())
    this.themeObserver.observe(document.body, {attributes: true, attributeFilter: ['class']})
  },

  beforeDestroy() {
    if (this.themeObserver) this.themeObserver.disconnect()
  },

  methods: {
    formatDateTime,

    detectTheme() {
      this.isDark = document.body.classList.contains('dark-only')
    },

    async loadSources() {
      this.sourcesLoading = true
      this.sourcesError = null
      try {
        const {data} = await analyticsApi.getSources()
        this.source = (data || []).find(s => s.id === this.sourceId) || null
        if (!this.source) {
          this.sourcesError = {
            icon: 'fa-question-circle',
            title: 'Источник не найден',
            text: this.page.missing,
          }
          return
        }
        const reports = this.source.reports || []
        if (!reports.some(r => r.id === this.tab)) this.tab = reports[0]?.id || ''
        this.syncQuery()
        if (this.source.enabled && this.tab) await this.loadReport()
      } catch (e) {
        this.source = null
        this.sourcesError = describeServiceError(e)
      } finally {
        this.sourcesLoading = false
      }
    },

    async loadReport() {
      const key = this.reportKey
      const cached = this.reports[key]
      if (cached && cached.state !== 'error') return

      const [tab, period] = [this.tab, this.period]
      this.$set(this.reports, key, {state: 'loading'})
      try {
        const sourceId = this.sourceId
        const res = await analyticsApi.getReport(sourceId, tab, period)
        if (res.status === 202) {
          this.$set(this.reports, key, {state: 'pending'})
          return
        }
        this.$set(this.reports, key, {
          state: 'ready',
          data: sourceId === 'rum' ? unwrapRumReport(res.data.data) : (res.data.data || {}),
          fetchedAt: res.data.fetchedAt,
          stale: res.data.stale,
        })
      } catch (e) {
        // Права и доступность сервиса уже проверены списком источников;
        // здесь ошибка относится к одному отчёту и не прячет остальные вкладки.
        this.$set(this.reports, key, {state: 'error', error: describeServiceError(e).text})
      }
    },

    setPeriod(value) {
      if (this.period === value) return
      this.period = value
      this.syncQuery()
      this.loadReport()
    },

    setTab(id) {
      if (this.tab === id) return
      this.tab = id
      this.syncQuery()
      this.loadReport()
    },

    reload() {
      // Повторный список источников обновляет «когда собрано» и ошибку источника;
      // кэш отчётов сбрасывается целиком, иначе соседние вкладки покажут старое.
      this.reports = {}
      this.loadSources()
    },

    // Ссылку на конкретную вкладку и период удобно кинуть тестировщику.
    syncQuery() {
      const query = {...this.$route.query, tab: this.tab, period: this.period}
      if (query.tab === this.$route.query.tab && query.period === this.$route.query.period) return
      this.$router.replace({query}).catch(() => {})
    },
  },
}
</script>

<style lang="scss">
@import "~@/assets/scss/tournament-form.scss";
@import "~@/assets/scss/analytics.scss";
</style>
